/**
 * NT-1376 — the PDP's Subscribe & Save / One-Time Purchase choice.
 *
 * Deliberately dumb: it never decides WHICH plan a variant may be sold under.
 * That rule lives in Liquid (see the resolver in sections/main-product.liquid,
 * a port of app/lib/product/subscriptionPlan.ts in nativepath-v2) and arrives
 * here as a pre-resolved, pre-formatted map keyed by variant id. This script
 * only tracks which row is selected and writes the add-to-cart form's inputs.
 *
 * It also never formats money or computes a discount, so the price a shopper
 * reads can't drift from the price Shopify charges.
 */
(function () {
  "use strict";

  /**
   * The add-to-cart form state for one (variant, selection) pair.
   *
   * Pure on purpose — this is the part BI depends on (`_is-subscription` and
   * `_subscription-discount-percentage` feed Salesforce and Redshift), so it is
   * unit-tested offline in tests/unit/subscription-selector.spec.ts.
   *
   * A variant with no plan always resolves to a one-time purchase, whatever the
   * radio says: selecting a plan the variant has no allocation for makes
   * /cart/add fail rather than silently sell the wrong thing.
   */
  function stateFor(plans, variantId, subscribed) {
    const entry = (plans && plans[variantId]) || null;
    const plan = (entry && entry.plan) || null;
    const active = Boolean(plan) && Boolean(subscribed);

    return {
      entry: entry,
      plan: plan,
      subscribed: active,
      sellingPlan: active ? String(plan.id) : "",
      isSubscription: active ? "1" : "0",
      discountPercentage: active ? plan.percent : "0.0",
    };
  }

  function setText(node, value) {
    if (node) node.textContent = value == null ? "" : value;
  }

  function setStrikethrough(node, value) {
    if (!node) return;
    node.textContent = value == null ? "" : value;
    node.hidden = value == null;
  }

  class SubscriptionSelector extends HTMLElement {
    connectedCallback() {
      if (!this.initialized) this.setup();
      // Re-added on every connect so a moved element keeps hearing variant
      // changes; the bound reference is stable, so this is a no-op the rest of
      // the time.
      document.addEventListener("change", this.onFormChange);
    }

    setup() {
      this.initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.formId;
      this.variantId = this.dataset.variantId;
      this.forced = this.hasAttribute("data-force-subscription");

      this.plans = this.readPlans();

      this.planInput = this.querySelector('input[name="selling_plan"]');
      this.flagInput = this.querySelector("[data-subscription-flag]");
      this.discountInput = this.querySelector("[data-subscription-discount]");

      this.subscribeRow = this.querySelector("[data-subscription-row]");
      this.oneTimeRow = this.querySelector("[data-one-time-row]");
      this.subscribeRadio = this.querySelector('input[value="subscription"]');
      this.oneTimeRadio = this.querySelector(
        'input[value="one-time-purchase"]',
      );

      // The flag that drives which price is visible goes on an ancestor, not on
      // the price node itself: `VariantSelects.renderProductInfo()` in global.js
      // overwrites `#price-<section>`'s innerHTML on every variant change, so
      // anything we set inside it would be thrown away moments later.
      this.scope =
        document.getElementById("ProductInfo-" + this.sectionId) ||
        this.parentElement;

      this.onRadioChange = this.onRadioChange.bind(this);
      // Dawn dispatches a bubbling `change` from the form's `[name=id]` input in
      // `updateVariantInput()`. Listening on the document rather than the form
      // keeps this working even though the selector lives outside <form>.
      this.onFormChange = this.onFormChange.bind(this);

      [this.subscribeRadio, this.oneTimeRadio].forEach((radio) => {
        if (radio) radio.addEventListener("change", this.onRadioChange);
      });

      this.subscribed = this.forced || this.wantsSubscriptionFromUrl();
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener("change", this.onFormChange);
    }

    readPlans() {
      const script = this.querySelector("script[data-subscription-plans]");
      if (!script) return {};
      try {
        return JSON.parse(script.textContent) || {};
      } catch (error) {
        console.error(
          "subscription-selector: could not parse plan data",
          error,
        );
        return {};
      }
    }

    /**
     * nativepath.com's mega menu links to `?subscription=true`, and those URLs
     * can land here, so the theme honours the same parameter. Default is
     * one-time, matching ProductForm.tsx in nativepath-v2.
     */
    wantsSubscriptionFromUrl() {
      try {
        return (
          new URLSearchParams(window.location.search).get("subscription") ===
          "true"
        );
      } catch (error) {
        return false;
      }
    }

    onRadioChange(event) {
      this.subscribed = event.target.value === "subscription";
      this.render();
    }

    onFormChange(event) {
      const input = event.target;
      if (!input || input.name !== "id") return;
      if (!input.form || input.form.id !== this.formId) return;
      if (input.value === this.variantId) return;

      this.variantId = input.value;
      this.dataset.variantId = input.value;
      this.render();
    }

    render() {
      const state = stateFor(this.plans, this.variantId, this.subscribed);

      // A variant with no plan can't be subscribed to, so fall back rather than
      // leave a checked radio that posts a one-time line.
      if (!state.plan) this.subscribed = false;

      this.hidden = !this.hasAnyPlan();

      if (this.planInput) {
        this.planInput.value = state.sellingPlan;
        this.planInput.disabled = !state.subscribed;
      }
      if (this.flagInput) this.flagInput.value = state.isSubscription;
      if (this.discountInput)
        this.discountInput.value = state.discountPercentage;

      if (this.subscribeRow) this.subscribeRow.hidden = !state.plan;
      if (this.subscribeRadio) this.subscribeRadio.checked = state.subscribed;
      if (this.oneTimeRadio) this.oneTimeRadio.checked = !state.subscribed;
      // The one-time row is only hidden on a subscription-only product, and only
      // while that product actually has a plan to sell instead.
      if (this.oneTimeRow)
        this.oneTimeRow.hidden = this.forced && Boolean(state.plan);

      this.renderPrices(state);

      if (this.scope) {
        if (state.subscribed) {
          this.scope.setAttribute("data-subscription-selected", "");
        } else {
          this.scope.removeAttribute("data-subscription-selected");
        }
      }
    }

    renderPrices(state) {
      const entry = state.entry;
      if (!entry) return;

      setText(this.querySelector("[data-one-time-row-price]"), entry.price);
      setStrikethrough(
        this.querySelector("[data-one-time-row-compare]"),
        entry.compareAt,
      );

      if (!state.plan) return;
      setText(
        this.querySelector("[data-subscription-title]"),
        state.plan.title,
      );
      setText(
        this.querySelector("[data-subscription-row-price]"),
        state.plan.price,
      );
      setStrikethrough(
        this.querySelector("[data-subscription-row-compare]"),
        entry.price,
      );
    }

    hasAnyPlan() {
      return Object.keys(this.plans).some((key) =>
        Boolean(this.plans[key] && this.plans[key].plan),
      );
    }
  }

  if (
    typeof customElements !== "undefined" &&
    !customElements.get("subscription-selector")
  ) {
    customElements.define("subscription-selector", SubscriptionSelector);
  }

  // Exposed for the offline unit tests; nothing in the theme reads it.
  window.NativePathSubscription = { stateFor: stateFor };
})();
